/**
 * What the API says about an account's compliance, read in one place.
 *
 * The `auth` block travels in the user payload (`/user/me` and every login
 * response). It is computed **server-side**, dates included: the window is the
 * account's own, opened at its first login under the rule, and the interface never
 * recomputes it — otherwise there would be two, the one that withholds the session
 * and the one that is displayed, and they would drift.
 */

/** `ok`: nothing owed. `due`: only the card missing, window open. `required`: enrollment session. */
export type AuthSetupState = "ok" | "due" | "required";

export type AuthFactor = "password" | "totp" | "psc";

export interface AuthPolicyPayload {
  state: AuthSetupState;
  /** What is missing, in the order the wizard asks for it (password first, card last). */
  missing_factors: AuthFactor[];
  /** Last day (YYYY-MM-DD, inclusive) for a doctor to log in without a linked card, while it is owed. */
  psc_deadline: string | null;
  /** True for a doctor on an instance offering Pro Santé Connect. */
  psc_required: boolean;
  psc_linked: boolean;
  exempt: boolean;
}

/**
 * The account's `auth` block, or null until it has been served.
 *
 * Null is not "compliant", it is "not known yet". Callers treat its absence as
 * "render nothing", never as "all is well" — a genuinely non-compliant account is
 * stopped by the server anyway.
 */
export function readAuthPolicy(user: unknown): AuthPolicyPayload | null {
  const block = (user as { auth?: AuthPolicyPayload } | null)?.auth;
  return block && typeof block.state === "string" ? block : null;
}

/** True while password or TOTP is still owed. */
export function owesTwoFactors(policy: AuthPolicyPayload): boolean {
  return (
    policy.missing_factors.includes("password") ||
    policy.missing_factors.includes("totp")
  );
}

/** The deadline as a French reader expects to see it written, or null. */
export function formatDeadline(deadline: string | null): string | null {
  if (!deadline) return null;
  const parsed = new Date(`${deadline}T00:00:00`);
  if (Number.isNaN(parsed.getTime())) return null;
  return parsed.toLocaleDateString("fr-FR", {
    day: "numeric",
    month: "long",
    year: "numeric",
  });
}
